import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { AuditAction } from '../model/audit.models';
import { AuditService } from './audit.service';
import { ConnectivityService } from './connectivity.service';
import { DeviceIdentityService } from './device-identity.service';

const AUDIT_ENDPOINT = `${environment.backendApiBaseUrl}/audit/events`;
const AUTH_ENDPOINT_PREFIX = `${environment.backendApiBaseUrl}/auth/`;

/**
 * Stamps every API call with the audit correlation headers and records every completed HTTP
 * action. Authentication and authorization failures are classified as APP_LEVEL by the server.
 */
export const auditInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const audit = inject(AuditService);
  const deviceIdentity = inject(DeviceIdentityService);
  const connectivity = inject(ConnectivityService);
  const auth = inject(AuthService);

  // The audit upload itself must not be audited, or a failure would queue an event per retry.
  const isAuditUpload = request.url.startsWith(AUDIT_ENDPOINT);
  const isBackendCall = request.url.startsWith(environment.backendApiBaseUrl);

  if (!isBackendCall) {
    return next(request);
  }

  const startedAt = Date.now();
  const correlationId = createId();

  const headers: Record<string, string> = {
    'X-Correlation-Id': correlationId,
    'X-Transaction-Id': audit.transactionId(),
    'X-Request-Id': createId(),
    'X-Device-Id': deviceIdentity.deviceIdSync(),
    'X-Device-Info': deviceIdentity.deviceInfo(),
    'X-Capture-Mode': connectivity.currentOnline ? 'ONLINE' : 'OFFLINE',
    'X-Client-Event-Time': new Date().toISOString(),
    'X-CSRF-Token': createCsrfToken(),
  };

  // Attribution comes from this token: without it every audit row is written with a null UserID.
  // Auth endpoints are excluded - they establish the session and must not require one.
  if (!request.url.startsWith(AUTH_ENDPOINT_PREFIX) && !request.headers.has('Authorization')) {
    const token = auth.currentAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const decorated = request.clone({ setHeaders: headers });

  return next(decorated).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          if (!isAuditUpload) {
            audit.track({
              category: 'DATA_ACCESS',
              action: request.method === 'GET' ? AuditAction.HTTP_GET : `HTTP_${request.method}`,
              httpMethod: request.method,
              requestPath: pathOf(request.url),
              httpStatus: event.status,
              message: `${request.method} request completed.`,
              detail: {
                method: request.method,
                path: pathOf(request.url),
                status: event.status,
                correlationId,
              },
            });
          }
        }
      },
      error: (error: unknown) => {
        if (isAuditUpload || !(error instanceof HttpErrorResponse)) return;
        recordFailure(audit, error, request, correlationId);
      },
    }),
  );
};

function recordFailure(
  audit: AuditService,
  error: HttpErrorResponse,
  request: HttpRequest<unknown>,
  correlationId: string,
): void {
  // A status of 0 means the network was unavailable, not that the server denied anything.
  if (error.status === 0) return;

  const isAuthFailure = error.status === 401 || error.status === 403;
  const isRateLimited = error.status === 429;
  if (!isAuthFailure && !isRateLimited) {
    audit.track({
      correlationId,
      category: 'DATA_ACCESS',
      action: `HTTP_${request.method}`,
      severity: 'ERROR',
      outcome: 'FAILURE',
      httpMethod: request.method,
      requestPath: pathOf(request.url),
      httpStatus: error.status,
      message: `${request.method} request failed with status ${error.status}.`,
      detail: {
        method: request.method,
        path: pathOf(request.url),
        status: error.status,
        correlationId,
      },
    });
    return;
  }

  audit.track({
    logSource: isAuthFailure || isRateLimited ? 'APP_LEVEL' : undefined,
    correlationId,
    category: isRateLimited ? 'SECURITY' : 'AUTHORIZATION',
    action: isRateLimited ? AuditAction.SYNC_FAILED : AuditAction.AUTHORIZATION_DENIED,
    severity: isRateLimited ? 'CRITICAL' : 'WARNING',
    outcome: 'FAILURE',
    authorizationDecision: 'DENY',
    httpMethod: request.method,
    requestPath: pathOf(request.url),
    httpStatus: error.status,
    message: isRateLimited
      ? 'Request rejected: synchronisation threshold exceeded.'
      : `Request denied by the API with status ${error.status}.`,
    detail: {
      method: request.method,
      path: pathOf(request.url),
      status: error.status,
      correlationId,
    },
  });
}

function pathOf(url: string): string {
  try {
    return new URL(url, globalThis.location?.origin).pathname;
  } catch {
    return url;
  }
}

function createId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`;
}

function createCsrfToken(): string {
  // Generate a secure random token for CSRF protection.
  // The token is per-request and validated server-side to prevent replay attacks.
  return createId();
}
